/**
 * Master Screen
 *
 * Displays the filtered master dataset as a table. The user can pick
 * which canonical columns to show by clicking column names in the
 * left panel. The visible column selection is persisted to the project.
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getMasterData } from "Redux/data/actions";
import { saveProject } from "Redux/project/actions";
import { canonicals } from "Data/canonicals.js";
import SkeletonTable from "Components/Skeleton";

const DEFAULT_COLUMNS = [
  "Issue_Date",
  "Due_Date",
  "Close_Date",
  "Amount_Document_Currency",
  "Document_Number",
  "Document_Type",
];

const mandatory = canonicals
  .filter((c) => c.isMandatory)
  .map((c) => c.header)
  .sort();

const optional = canonicals
  .filter((c) => !c.isMandatory)
  .map((c) => c.header)
  .sort();

// -------------------------------------------------------------
const Master = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { master } = useSelector((state) => state.data);
  const { hasMaster, masterViewColumns } = useSelector((state) => state.project);
  const dispatch = useDispatch();

  const columns = masterViewColumns || DEFAULT_COLUMNS;

  useEffect(() => {
    if (hasMaster && !master) {
      setIsLoading(true);
      dispatch(getMasterData(() => setIsLoading(false)));
    }
  }, [hasMaster, master, dispatch]);

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  if (isLoading) return <SkeletonTable rows={10} cols={6} />;

  const visibleSet = new Set(columns);

  const addColumn = (col) => {
    const updated = [...columns, col];
    dispatch({ type: "SET_MASTER_VIEW_COLUMNS", columns: updated });
    dispatch(saveProject());
  };

  const removeColumn = (col) => {
    const updated = columns.filter((c) => c !== col);
    dispatch({ type: "SET_MASTER_VIEW_COLUMNS", columns: updated });
    dispatch(saveProject());
  };

  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
      {/* Left: Column picker */}
      <div style={{ flex: "0 0 220px", minWidth: "180px" }}>
        <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "var(--ev-text)", marginBottom: "0.4rem" }}>
          Mandatory
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginBottom: "0.75rem" }}>
          {mandatory.map((col) => {
            const active = visibleSet.has(col);
            return (
              <span
                key={col}
                className="tag"
                onClick={active ? undefined : () => addColumn(col)}
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  borderRadius: "5px",
                  cursor: active ? "default" : "pointer",
                  opacity: active ? 0.35 : 1,
                  backgroundColor: active ? "var(--ev-surface)" : "var(--ev-white)",
                  color: "var(--ev-text)",
                  border: "1px solid var(--ev-border)",
                  transition: "opacity 0.15s ease",
                }}
              >
                {col}
              </span>
            );
          })}
        </div>

        <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "var(--ev-text)", marginBottom: "0.4rem" }}>
          Optional
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
          {optional.map((col) => {
            const active = visibleSet.has(col);
            return (
              <span
                key={col}
                className="tag"
                onClick={active ? undefined : () => addColumn(col)}
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  borderRadius: "5px",
                  cursor: active ? "default" : "pointer",
                  opacity: active ? 0.35 : 1,
                  backgroundColor: active ? "var(--ev-surface)" : "var(--ev-white)",
                  color: "var(--ev-text)",
                  border: "1px solid var(--ev-border)",
                  transition: "opacity 0.15s ease",
                }}
              >
                {col}
              </span>
            );
          })}
        </div>
      </div>

      {/* Right: Data table */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {master ? (
          <>
            <div style={{
              fontSize: "0.72rem", color: "var(--ev-text-muted)", marginBottom: "0.4rem",
            }}>
              Showing {master.data.length} of {master.nrows.toLocaleString()} rows
            </div>
            <div className="table-container">
              <table className="table is-fullwidth is-striped is-bordered is-small-table">
                <thead>
                  <tr>
                    {columns.map((h) => (
                      <th key={h} style={{ whiteSpace: "nowrap" }}>
                        <div className="tags has-addons" style={{ marginBottom: 0 }}>
                          <span className="tag is-small is-info is-light" style={{ borderRadius: "4px" }}>
                            {h}
                            <button
                              className="delete is-small"
                              onClick={() => removeColumn(h)}
                              style={{ marginLeft: "0.25rem" }}
                            />
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {master.data.map((row, i) => (
                    <tr key={i}>
                      {columns.map((h) => (
                        <td key={`${i}-${h}`} style={{ whiteSpace: "nowrap" }}>
                          {clean(row[h])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div style={{ fontSize: "0.85rem", color: "var(--ev-text-muted)", fontStyle: "italic" }}>
            No data loaded.
          </div>
        )}
      </div>
    </div>
  );
};

export default Master;

/** Replace regular spaces with non-breaking spaces so table cells don't wrap mid-value. */
const clean = (str) => {
  if (typeof str !== "string") return str;
  if (str === null) return null;
  return str.replace(/ /g, "\u00A0");
};
